import React, { useEffect, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import AppointmentTable from "../components/AppointmentTable";
import DashboardFilter from "../components/DashboardFilter";

import { Icon } from "@iconify/react/dist/iconify.js";
import { API_URL } from "..";
import { Link } from "react-router-dom";
import { Spinner } from "react-bootstrap";

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 1,
};

const AppointmentPage = () => {
  const [data, setData] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const [stats, setStats] = useState({
    totalJobs: 0,
    newJobs: 0,
    activeJobs: 0,
    completedJobs: 0,
    declinedJobs: 0,
    closedJobs: 0,
    unAssigned: 0,
  });

  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  const [dateFilter, setDateFilter] = useState("all");
  const [customRange, setCustomRange] = useState({
    startDate: "",
    endDate: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState("all");

  const buildStatusUrl = (status) => {
    const params = new URLSearchParams();

    params.set("filter", dateFilter);

    if (dateFilter === "custom") {
      if (customRange.startDate) {
        params.set("startDate", customRange.startDate);
      }

      if (customRange.endDate) {
        params.set("endDate", customRange.endDate);
      }
    }

    if (searchTerm && String(searchTerm).trim() !== "") {
      params.set("search", String(searchTerm).trim());
      params.set("searchColumn", searchColumn || "all");
    }

    return `/jobs/${status}?${params.toString()}`;
  };

  const fetchAppointments = async (
    page = 1,
    limit = 25,
    filter = "all",
    range = {},
    search = searchTerm,
    column = searchColumn
  ) => {
    setLoadingStatus(true);

    try {
      const params = new URLSearchParams();

      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("filter", filter || "all");

      if (filter === "custom" && range.startDate && range.endDate) {
        params.set("startDate", range.startDate);
        params.set("endDate", range.endDate);
      }

      if (search && String(search).trim() !== "") {
        params.set("search", String(search).trim());
        params.set("searchColumn", column || "all");
      }

      const response = await fetch(
        `${API_URL}/api/job/fetch/all?${params.toString()}`,
        {
          method: "GET",
          headers: { Accept: "*/*" },
        }
      );

      const result = await response.json();

      if (response.status === 200) {
        setData(result.job || []);
        setStats(result.stats || {});
        setPagination(result.pagination || DEFAULT_PAGINATION);
      } else {
        setData([]);
        setStats({
          totalJobs: 0,
          newJobs: 0,
          activeJobs: 0,
          completedJobs: 0,
          declinedJobs: 0,
          closedJobs: 0,
          unAssigned: 0,
        });
        setPagination(DEFAULT_PAGINATION);
      }
    } catch (err) {
      console.error("Error fetching appointments:", err);
      setData([]);
      setPagination(DEFAULT_PAGINATION);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    fetchAppointments(1, pagination.limit, dateFilter, customRange, "", "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (newFilter) => {
    setDateFilter(newFilter);

    if (newFilter !== "custom") {
      fetchAppointments(
        1,
        pagination.limit,
        newFilter,
        {},
        searchTerm,
        searchColumn
      );
    }
  };

  const handleCustomApply = () => {
    if (!customRange.startDate || !customRange.endDate) return;

    fetchAppointments(
      1,
      pagination.limit,
      "custom",
      customRange,
      searchTerm,
      searchColumn
    );
  };

  const handleSearchSubmit = () => {
    fetchAppointments(
      1,
      pagination.limit,
      dateFilter,
      customRange,
      searchTerm,
      searchColumn
    );
  };

  const handleSearchClear = () => {
    setSearchTerm("");
    setSearchColumn("all");

    fetchAppointments(1, pagination.limit, dateFilter, customRange, "", "all");
  };

  const handlePageChange = (newPage) => {
    fetchAppointments(
      newPage,
      pagination.limit,
      dateFilter,
      customRange,
      searchTerm,
      searchColumn
    );
  };

  const handleLimitChange = (newLimit) => {
    fetchAppointments(
      1,
      newLimit,
      dateFilter,
      customRange,
      searchTerm,
      searchColumn
    );
  };

  return (
    <>
      <MasterLayout>
        <Breadcrumb title="Jobs" />

        <div className="mt-4 mb-20">
          <div className="col-xxl-12">
            <div className="mt-4 mb-3">
              <DashboardFilter
                filter={dateFilter}
                onFilterChange={handleFilterChange}
                customRange={customRange}
                setCustomRange={setCustomRange}
                onCustomApply={handleCustomApply}
                loading={loadingStatus}
              />
            </div>

            <div className="row gy-4">
              <Link className="col-xxl-3 col-sm-6" to={buildStatusUrl("total")}>
                <div>
                  <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 bg-gradient-end-1">
                    <div className="card-body p-0">
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                        <div className="d-flex align-items-center gap-2">
                          <span className="mb-0 w-48-px h-48-px bg-primary-600 flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6 mb-0">
                            <Icon icon="hugeicons:briefcase-01" className="icon" />
                          </span>

                          <div>
                            <span className="mb-2 fw-medium text-secondary-light text-sm">
                              Total Jobs
                            </span>
                            <h6 className="fw-semibold">{stats.totalJobs || 0}</h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>

              <Link className="col-xxl-3 col-sm-6" to={buildStatusUrl("un-assigned")}>
                <div>
                  <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 bg-gradient-end-2">
                    <div className="card-body p-0">
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                        <div className="d-flex align-items-center gap-2">
                          <span className="mb-0 w-48-px h-48-px bg-success-main flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6">
                            <Icon icon="hugeicons:briefcase-01" className="icon" />
                          </span>

                          <div>
                            <span className="mb-2 fw-medium text-secondary-light text-sm">
                              UnAssigned Jobs
                            </span>
                            <h6 className="fw-semibold">{stats.unAssigned || 0}</h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>

              <Link className="col-xxl-3 col-sm-6" to={buildStatusUrl("new")}>
                <div>
                  <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 bg-gradient-end-2">
                    <div className="card-body p-0">
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                        <div className="d-flex align-items-center gap-2">
                          <span className="mb-0 w-48-px h-48-px bg-success-main flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6">
                            <Icon icon="hugeicons:briefcase-01" className="icon" />
                          </span>

                          <div>
                            <span className="mb-2 fw-medium text-secondary-light text-sm">
                              New Jobs (Assigned)
                            </span>
                            <h6 className="fw-semibold">{stats.newJobs || 0}</h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>

              <Link className="col-xxl-3 col-sm-6" to={buildStatusUrl("active")}>
                <div>
                  <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 bg-gradient-end-3">
                    <div className="card-body p-0">
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                        <div className="d-flex align-items-center gap-2">
                          <span className="mb-0 w-48-px h-48-px bg-yellow text-white flex-shrink-0 d-flex justify-content-center align-items-center rounded-circle h6">
                            <Icon icon="hugeicons:briefcase-01" className="icon" />
                          </span>

                          <div>
                            <span className="mb-2 fw-medium text-secondary-light text-sm">
                              Active Jobs
                            </span>
                            <h6 className="fw-semibold">{stats.activeJobs || 0}</h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>

              <Link className="col-xxl-3 col-sm-6" to={buildStatusUrl("completed")}>
                <div>
                  <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 bg-gradient-end-4">
                    <div className="card-body p-0">
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                        <div className="d-flex align-items-center gap-2">
                          <span className="mb-0 w-48-px h-48-px bg-purple text-white flex-shrink-0 d-flex justify-content-center align-items-center rounded-circle h6">
                            <Icon icon="hugeicons:briefcase-01" className="icon" />
                          </span>

                          <div>
                            <span className="mb-2 fw-medium text-secondary-light text-sm">
                              Completed Jobs
                            </span>
                            <h6 className="fw-semibold">{stats.completedJobs || 0}</h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>

              <Link className="col-xxl-3 col-sm-6" to={buildStatusUrl("declined")}>
                <div>
                  <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 bg-gradient-end-5">
                    <div className="card-body p-0">
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                        <div className="d-flex align-items-center gap-2">
                          <span className="mb-0 w-48-px h-48-px bg-danger text-white flex-shrink-0 d-flex justify-content-center align-items-center rounded-circle h6">
                            <Icon icon="hugeicons:briefcase-01" className="icon" />
                          </span>

                          <div>
                            <span className="mb-2 fw-medium text-secondary-light text-sm">
                              Declined Jobs
                            </span>
                            <h6 className="fw-semibold">{stats.declinedJobs || 0}</h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>

              <Link className="col-xxl-3 col-sm-6" to={buildStatusUrl("closed")}>
                <div>
                  <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 bg-gradient-end-5">
                    <div className="card-body p-0">
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                        <div className="d-flex align-items-center gap-2">
                          <span className="mb-0 w-48-px h-48-px bg-pink text-white flex-shrink-0 d-flex justify-content-center align-items-center rounded-circle h6">
                            <Icon icon="hugeicons:briefcase-01" className="icon" />
                          </span>

                          <div>
                            <span className="mb-2 fw-medium text-secondary-light text-sm">
                              Closed Jobs
                            </span>
                            <h6 className="fw-semibold">{stats.closedJobs || 0}</h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {loadingStatus && (
          <div className="text-center mt-4">
            <Spinner />
          </div>
        )}

        {!loadingStatus && (
          <AppointmentTable
            data={data}
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            loading={loadingStatus}
            searchTerm={searchTerm}
            searchColumn={searchColumn}
            onSearchTermChange={setSearchTerm}
            onSearchColumnChange={setSearchColumn}
            onSearchSubmit={handleSearchSubmit}
            onSearchClear={handleSearchClear}
          />
        )}
      </MasterLayout>
    </>
  );
};

export default AppointmentPage;